import { useState, useEffect, useCallback } from 'react'
import type { UserLocation } from '@fuelfinder/shared'

/**
 * localStorage key for cached location
 */
const LOCATION_STORAGE_KEY = 'fuelfinder-location'

/**
 * Postcode.io API response types
 */
interface PostcodeResult {
  postcode: string
  latitude: number
  longitude: number
}

interface PostcodeResponse {
  status: number
  result: PostcodeResult | null
}

interface UseLocationReturn {
  location: UserLocation | null
  /** GPS location from device (null if not available or denied) */
  gpsLocation: UserLocation | null
  isLoading: boolean
  error: string | null
  setPostcode: (postcode: string) => void
  /** Set location directly from coordinates */
  setCoordinates: (lat: number, lng: number) => void
  /** Reset location to GPS location */
  resetToGps: () => void
  retry: () => void
}

/**
 * Load cached location from localStorage
 */
function loadCachedLocation(): UserLocation | null {
  try {
    const cached = localStorage.getItem(LOCATION_STORAGE_KEY)
    if (cached) {
      return JSON.parse(cached) as UserLocation
    }
  } catch {
    // Ignore parse errors
  }
  return null
}

/**
 * Save location to localStorage
 */
function saveLocation(location: UserLocation): void {
  try {
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location))
  } catch {
    // Ignore storage errors
  }
}

/**
 * Geocode a UK postcode using postcodes.io API
 */
async function geocodePostcode(postcode: string): Promise<UserLocation> {
  // Normalize postcode (remove spaces, uppercase)
  const normalized = postcode.replace(/\s+/g, '').toUpperCase()

  const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(normalized)}`)

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Postcode not found')
    }
    throw new Error(`Postcode lookup failed: ${response.status}`)
  }

  const data: PostcodeResponse = await response.json()

  if (!data.result) {
    throw new Error('Invalid postcode')
  }

  return {
    lat: data.result.latitude,
    lng: data.result.longitude,
    source: 'postcode',
    timestamp: Date.now(),
  }
}

/**
 * Hook for managing user location with parallel geo + postcode approach
 *
 * On mount:
 * 1. Load cached location from localStorage (instant)
 * 2. Simultaneously request geolocation API
 * 3. When geolocation succeeds, update location and save to localStorage
 *
 * Also provides setPostcode for manual postcode entry fallback
 */
export function useLocation(): UseLocationReturn {
  const [location, setLocation] = useState<UserLocation | null>(() => loadCachedLocation())
  const [gpsLocation, setGpsLocation] = useState<UserLocation | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Track if we've already requested geolocation to avoid duplicate requests
  const [geoRequested, setGeoRequested] = useState(false)

  // Request geolocation on mount
  useEffect(() => {
    if (geoRequested) return
    if (!('geolocation' in navigator)) return

    setGeoRequested(true)
    setIsLoading(true)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLocation: UserLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          source: 'geolocation',
          timestamp: Date.now(),
        }

        setLocation(newLocation)
        setGpsLocation(newLocation) // Also store as GPS location
        saveLocation(newLocation)
        setIsLoading(false)
        setError(null)
      },
      (geoError) => {
        setIsLoading(false)

        // Don't show error if we have cached location
        if (!location) {
          switch (geoError.code) {
            case GeolocationPositionError.PERMISSION_DENIED:
              setError('Location permission denied. Enter a postcode instead.')
              break
            case GeolocationPositionError.POSITION_UNAVAILABLE:
              setError('Location unavailable. Enter a postcode instead.')
              break
            case GeolocationPositionError.TIMEOUT:
              setError('Location request timed out. Enter a postcode instead.')
              break
            default:
              setError('Could not get location. Enter a postcode instead.')
          }
        }
      },
      {
        enableHighAccuracy: false, // Don't need GPS-level accuracy
        timeout: 10000, // 10 second timeout
        maximumAge: 60000, // Accept cached position up to 1 minute old
      }
    )
  }, [geoRequested, location])

  // Handle postcode input
  const setPostcode = useCallback(async (postcode: string) => {
    if (!postcode.trim()) return

    setIsLoading(true)
    setError(null)

    try {
      const newLocation = await geocodePostcode(postcode)
      setLocation(newLocation)
      saveLocation(newLocation)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to lookup postcode')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Retry geolocation
  const retry = useCallback(() => {
    setGeoRequested(false)
    setError(null)
  }, [])

  // Set location directly from coordinates
  const setCoordinates = useCallback((lat: number, lng: number) => {
    const newLocation: UserLocation = {
      lat,
      lng,
      source: 'postcode', // Treat as manual entry
      timestamp: Date.now(),
    }
    setLocation(newLocation)
    saveLocation(newLocation)
  }, [])

  // Reset location to GPS location
  const resetToGps = useCallback(() => {
    if (gpsLocation) {
      setLocation(gpsLocation)
      saveLocation(gpsLocation)
    }
  }, [gpsLocation])

  return {
    location,
    gpsLocation,
    isLoading,
    error,
    setPostcode,
    setCoordinates,
    resetToGps,
    retry,
  }
}
